import React, { useState, useEffect } from 'react';
import { Interest } from '../types';
import {
  StepContainer,
  StepTitle,
  StepDescription,
  InterestsContainer,
  InterestCategories,
  CategoryTab,
  InterestGrid,
  InterestItem,
  InterestIcon,
  InterestName,
  SelectedInterests,
  SelectedInterest,
  InterestSearch,
  SearchInput,
  InterestCounter,
  ValidationMessage
} from '../../../styles/Profile/ProfileSetup.styles';

interface InterestsStepProps {
  data: Partial<{ interests: Interest[] }>;
  onChange: (data: { interests: Interest[] }, isValid: boolean) => void;
  onNext: () => void;
  onBack: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

const INTEREST_CATEGORIES = [
  { id: 'hobbies', name: 'Hobbies', icon: '🎨' },
  { id: 'sports', name: 'Sports', icon: '⚽' },
  { id: 'music', name: 'Music', icon: '🎵' },
  { id: 'movies', name: 'Movies', icon: '🎬' },
  { id: 'books', name: 'Books', icon: '📚' },
  { id: 'travel', name: 'Travel', icon: '✈️' },
  { id: 'food', name: 'Food', icon: '🍕' },
  { id: 'other', name: 'Other', icon: '🌟' }
];

const INTERESTS_DATA: Record<string, Interest[]> = {
  hobbies: [
    { id: 'photography', name: 'Photography', category: 'hobbies', icon: '📸' },
    { id: 'painting', name: 'Painting', category: 'hobbies', icon: '🎨' },
    { id: 'drawing', name: 'Drawing', category: 'hobbies', icon: '✏️' },
    { id: 'gardening', name: 'Gardening', category: 'hobbies', icon: '🌱' },
    { id: 'crafting', name: 'Crafting', category: 'hobbies', icon: '🧵' },
    { id: 'pottery', name: 'Pottery', category: 'hobbies', icon: '🏺' },
    { id: 'woodworking', name: 'Woodworking', category: 'hobbies', icon: '🔨' },
    { id: 'knitting', name: 'Knitting', category: 'hobbies', icon: '🧶' }
  ],
  sports: [
    { id: 'running', name: 'Running', category: 'sports', icon: '🏃' },
    { id: 'cycling', name: 'Cycling', category: 'sports', icon: '🚴' },
    { id: 'swimming', name: 'Swimming', category: 'sports', icon: '🏊' },
    { id: 'tennis', name: 'Tennis', category: 'sports', icon: '🎾' },
    { id: 'basketball', name: 'Basketball', category: 'sports', icon: '🏀' },
    { id: 'soccer', name: 'Soccer', category: 'sports', icon: '⚽' },
    { id: 'yoga', name: 'Yoga', category: 'sports', icon: '🧘' },
    { id: 'hiking', name: 'Hiking', category: 'sports', icon: '🥾' }
  ],
  music: [
    { id: 'rock', name: 'Rock', category: 'music', icon: '🎸' },
    { id: 'pop', name: 'Pop', category: 'music', icon: '🎤' },
    { id: 'jazz', name: 'Jazz', category: 'music', icon: '🎷' },
    { id: 'classical', name: 'Classical', category: 'music', icon: '🎼' },
    { id: 'electronic', name: 'Electronic', category: 'music', icon: '🎛️' },
    { id: 'hip-hop', name: 'Hip-Hop', category: 'music', icon: '🎧' },
    { id: 'country', name: 'Country', category: 'music', icon: '🎻' },
    { id: 'indie', name: 'Indie', category: 'music', icon: '🎵' }
  ],
  movies: [
    { id: 'action', name: 'Action', category: 'movies', icon: '💥' },
    { id: 'comedy', name: 'Comedy', category: 'movies', icon: '😂' },
    { id: 'drama', name: 'Drama', category: 'movies', icon: '🎭' },
    { id: 'horror', name: 'Horror', category: 'movies', icon: '👻' },
    { id: 'sci-fi', name: 'Sci-Fi', category: 'movies', icon: '🚀' },
    { id: 'romance', name: 'Romance', category: 'movies', icon: '💕' },
    { id: 'thriller', name: 'Thriller', category: 'movies', icon: '🔍' },
    { id: 'documentary', name: 'Documentary', category: 'movies', icon: '📹' }
  ],
  books: [
    { id: 'fiction', name: 'Fiction', category: 'books', icon: '📖' },
    { id: 'non-fiction', name: 'Non-Fiction', category: 'books', icon: '📚' },
    { id: 'mystery', name: 'Mystery', category: 'books', icon: '🔍' },
    { id: 'romance', name: 'Romance', category: 'books', icon: '💕' },
    { id: 'sci-fi', name: 'Sci-Fi', category: 'books', icon: '🚀' },
    { id: 'fantasy', name: 'Fantasy', category: 'books', icon: '🧙' },
    { id: 'biography', name: 'Biography', category: 'books', icon: '👤' },
    { id: 'self-help', name: 'Self-Help', category: 'books', icon: '💪' }
  ],
  travel: [
    { id: 'beach', name: 'Beach', category: 'travel', icon: '🏖️' },
    { id: 'mountains', name: 'Mountains', category: 'travel', icon: '🏔️' },
    { id: 'cities', name: 'Cities', category: 'travel', icon: '🏙️' },
    { id: 'nature', name: 'Nature', category: 'travel', icon: '🌲' },
    { id: 'culture', name: 'Culture', category: 'travel', icon: '🏛️' },
    { id: 'adventure', name: 'Adventure', category: 'travel', icon: '🧗' },
    { id: 'food-travel', name: 'Food Travel', category: 'travel', icon: '🍜' },
    { id: 'photography', name: 'Travel Photography', category: 'travel', icon: '📸' }
  ],
  food: [
    { id: 'cooking', name: 'Cooking', category: 'food', icon: '👨‍🍳' },
    { id: 'baking', name: 'Baking', category: 'food', icon: '🧁' },
    { id: 'wine', name: 'Wine', category: 'food', icon: '🍷' },
    { id: 'coffee', name: 'Coffee', category: 'food', icon: '☕' },
    { id: 'tea', name: 'Tea', category: 'food', icon: '🍵' },
    { id: 'cocktails', name: 'Cocktails', category: 'food', icon: '🍸' },
    { id: 'fine-dining', name: 'Fine Dining', category: 'food', icon: '🍽️' },
    { id: 'street-food', name: 'Street Food', category: 'food', icon: '🌮' }
  ],
  other: [
    { id: 'volunteering', name: 'Volunteering', category: 'other', icon: '🤝' },
    { id: 'meditation', name: 'Meditation', category: 'other', icon: '🧘' },
    { id: 'learning', name: 'Learning', category: 'other', icon: '📚' },
    { id: 'technology', name: 'Technology', category: 'other', icon: '💻' },
    { id: 'pets', name: 'Pets', category: 'other', icon: '🐕' },
    { id: 'fitness', name: 'Fitness', category: 'other', icon: '💪' },
    { id: 'gaming', name: 'Gaming', category: 'other', icon: '🎮' },
    { id: 'art', name: 'Art', category: 'other', icon: '🎨' }
  ]
};

const MIN_INTERESTS = 3;
const MAX_INTERESTS = 20;

export const InterestsStep: React.FC<InterestsStepProps> = ({
  data,
  onChange,
  onNext,
  onBack,
  isFirstStep,
  isLastStep
}) => {
  const [selectedInterests, setSelectedInterests] = useState<Interest[]>(data.interests || []);
  const [activeCategory, setActiveCategory] = useState('hobbies');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredInterests, setFilteredInterests] = useState<Interest[]>([]);

  const validateInterests = (): boolean => {
    return selectedInterests.length >= MIN_INTERESTS && selectedInterests.length <= MAX_INTERESTS;
  };

  const handleInterestToggle = (interest: Interest) => {
    const isSelected = selectedInterests.some(selected => selected.id === interest.id);
    
    if (isSelected) {
      setSelectedInterests(prev => prev.filter(selected => selected.id !== interest.id));
    } else {
      if (selectedInterests.length < MAX_INTERESTS) {
        setSelectedInterests(prev => [...prev, interest]);
      }
    }
  };

  const handleRemoveInterest = (interestId: string) => {
    setSelectedInterests(prev => prev.filter(interest => interest.id !== interestId));
  };

  const filterInterests = (interests: Interest[], searchTerm: string): Interest[] => {
    if (!searchTerm) return interests;
    return interests.filter(interest => 
      interest.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  useEffect(() => {
    const categoryInterests = INTERESTS_DATA[activeCategory] || [];
    const filtered = filterInterests(categoryInterests, searchTerm);
    setFilteredInterests(filtered);
  }, [activeCategory, searchTerm]);

  useEffect(() => {
    const isValid = validateInterests();
    onChange({ interests: selectedInterests }, isValid);
  }, [selectedInterests]);

  const getCategoryInterests = (categoryId: string): Interest[] => {
    return INTERESTS_DATA[categoryId] || [];
  };

  return (
    <StepContainer>
      <StepTitle>Select your interests</StepTitle>
      <StepDescription>
        Choose interests that represent you. This helps others find common ground and start meaningful conversations.
      </StepDescription>

      <InterestsContainer>
        <InterestSearch>
          <SearchInput
            type="text"
            placeholder="Search interests..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InterestSearch>

        <InterestCategories>
          {INTEREST_CATEGORIES.map(category => (
            <CategoryTab
              key={category.id}
              $isActive={activeCategory === category.id}
              onClick={() => setActiveCategory(category.id)}
            >
              <InterestIcon>{category.icon}</InterestIcon>
              <InterestName>{category.name}</InterestName>
            </CategoryTab>
          ))}
        </InterestCategories>

        <InterestGrid>
          {filteredInterests.map(interest => {
            const isSelected = selectedInterests.some(selected => selected.id === interest.id);
            return (
              <InterestItem
                key={interest.id}
                $isSelected={isSelected}
                onClick={() => handleInterestToggle(interest)}
              >
                <InterestIcon>{interest.icon}</InterestIcon>
                <InterestName>{interest.name}</InterestName>
              </InterestItem>
            );
          })}
        </InterestGrid>

        <SelectedInterests>
          <h4>Selected Interests ({selectedInterests.length}/{MAX_INTERESTS}):</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '1rem' }}>
            {selectedInterests.map(interest => (
              <SelectedInterest key={interest.id}>
                <InterestIcon>{interest.icon}</InterestIcon>
                <InterestName>{interest.name}</InterestName>
                <button
                  onClick={() => handleRemoveInterest(interest.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    marginLeft: '0.5rem'
                  }}
                >
                  ×
                </button>
              </SelectedInterest>
            ))}
          </div>
        </SelectedInterests>

        <InterestCounter>
          {selectedInterests.length < MIN_INTERESTS ? (
            <ValidationMessage>
              Please select at least {MIN_INTERESTS} interests to continue.
            </ValidationMessage>
          ) : selectedInterests.length > MAX_INTERESTS ? (
            <ValidationMessage>
              You can select up to {MAX_INTERESTS} interests.
            </ValidationMessage>
          ) : (
            <div style={{ color: '#2ed573' }}>
              ✓ Great! You have selected {selectedInterests.length} interests.
            </div>
          )}
        </InterestCounter>
      </InterestsContainer>
    </StepContainer>
  );
};
